<script setup lang="ts">
import { computed } from "vue";
import type { CountdownStyleProps } from "./shared";
import { resolveLayoutTokens, speedDuration } from "./shared";

const props = defineProps<CountdownStyleProps>();
const layout = computed(() => resolveLayoutTokens(props.block, props.viewport));
const tickMs = computed(() => speedDuration(props.block.animationSpeed, 600));

const containerStyle = computed(
  () =>
    ({
      background: props.block.backgroundColor || "#0c0c0c",
      fontFamily:
        props.block.fontFamily ||
        "'JetBrains Mono', 'Fira Code', 'Cascadia Code', monospace",
      padding: `${layout.value.padding}px`,
      minHeight: layout.value.fullscreen ? "60vh" : undefined,
      "--cd-tick": `${tickMs.value}ms`,
    }) as Record<string, string | number | undefined>,
);

const fontSize = computed(
  () => `${props.block.digitFontSize * 0.55 * layout.value.digitScale}px`,
);
const digitFontSize = computed(
  () => `${props.block.digitFontSize * layout.value.digitScale}px`,
);

const segmentLine = computed(() =>
  props.segments.map((s) => `${s.value}${s.label[0] ?? ""}`).join(" "),
);
</script>

<template>
  <div class="cd-cli" :style="containerStyle">
    <div class="cd-cli__chrome" :style="{ fontSize: fontSize }">
      <span class="cd-cli__dot cd-cli__dot--r" />
      <span class="cd-cli__dot cd-cli__dot--y" />
      <span class="cd-cli__dot cd-cli__dot--g" />
      <span class="cd-cli__title">~/countdown — bash</span>
    </div>
    <div class="cd-cli__body">
      <div class="cd-cli__line" :style="{ fontSize: fontSize }">
        <span class="cd-cli__user">user@launch</span>
        <span class="cd-cli__sep">:</span>
        <span class="cd-cli__path">~</span>
        <span class="cd-cli__sep">$</span>
        <span class="cd-cli__cmd">countdown --remaining</span>
      </div>
      <div class="cd-cli__output" :style="{ fontSize: digitFontSize }">
        <span
          v-for="(seg, i) in segments"
          :key="seg.key"
          class="cd-cli__seg"
          :style="{ animationDelay: `${i * 60}ms` }"
        >
          <span :key="seg.value" class="cd-cli__num">{{ seg.value }}</span>
          <span class="cd-cli__unit">{{
            (seg.label[0] || "").toLowerCase()
          }}</span>
        </span>
      </div>
      <div class="cd-cli__line" :style="{ fontSize: fontSize }">
        <span class="cd-cli__user">user@launch</span>
        <span class="cd-cli__sep">:</span>
        <span class="cd-cli__path">~</span>
        <span class="cd-cli__sep">$</span>
        <span class="cd-cli__caret">▌</span>
      </div>
      <span class="cd-cli__hidden" aria-hidden="true">{{ segmentLine }}</span>
    </div>
  </div>
</template>

<style scoped>
.cd-cli {
  display: flex;
  flex-direction: column;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #1f1f1f;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.45);
  color: #d1fae5;
}
.cd-cli__chrome {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: #1a1a1a;
  border-bottom: 1px solid #0a0a0a;
  color: #6b7280;
}
.cd-cli__dot {
  width: 11px;
  height: 11px;
  border-radius: 50%;
  display: inline-block;
}
.cd-cli__dot--r {
  background: #ff5f57;
}
.cd-cli__dot--y {
  background: #febc2e;
}
.cd-cli__dot--g {
  background: #28c840;
}
.cd-cli__title {
  margin-left: 12px;
  font-weight: 500;
}
.cd-cli__body {
  padding: 14px 18px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.cd-cli__line {
  display: flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
}
.cd-cli__user {
  color: #34d399;
  font-weight: 600;
}
.cd-cli__path {
  color: #60a5fa;
}
.cd-cli__sep {
  color: #6b7280;
  margin: 0 2px;
}
.cd-cli__cmd {
  color: #f9fafb;
  margin-left: 6px;
}
.cd-cli__output {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 0.5em;
  font-weight: 700;
  color: #4ade80;
}
.cd-cli__seg {
  display: inline-flex;
  align-items: baseline;
  gap: 2px;
  font-variant-numeric: tabular-nums;
  text-shadow: 0 0 8px rgba(74, 222, 128, 0.5);
  animation: cd-cli-type 700ms ease-out backwards;
}
@keyframes cd-cli-type {
  from {
    opacity: 0;
    transform: translateX(-6px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}
.cd-cli__num {
  display: inline-block;
  animation: cd-cli-tick var(--cd-tick, 600ms) ease-out;
}
@keyframes cd-cli-tick {
  from {
    color: #fff;
    text-shadow: 0 0 14px rgba(74, 222, 128, 0.9);
  }
  to {
    color: #4ade80;
  }
}
.cd-cli__unit {
  font-size: 0.45em;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  text-shadow: none;
}
.cd-cli__caret {
  display: inline-block;
  width: 0.6em;
  color: #4ade80;
  animation: cd-cli-blink 1s steps(2, end) infinite;
}
@keyframes cd-cli-blink {
  50% {
    opacity: 0;
  }
}
.cd-cli__hidden {
  display: none;
}
</style>
